import { INTERVAL_DAYS, MAX_STAGE, findTerm, formatForCopy, mastery } from '@engpod/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../components/Icon';
import { PageHead } from '../components/PageHead';
import { api, errorText } from '../lib/api';
import { useAuth } from '../lib/auth';
import { playClip } from '../lib/clip';
import { copyText } from '../lib/format';
import type { VocabContext, VocabItem } from '../types';

type Status = '' | 'due' | 'learning' | 'mastered';
const STATUS: [Status, string][] = [
  ['', '全部'],
  ['due', '待复习'],
  ['learning', '学习中'],
  ['mastered', '已掌握'],
];
const MASTERY_LABEL = { new: '新加', learning: '学习中', mastered: '已掌握' } as const;
const DAY = 86_400_000;

/** "今天 / 最近 7 天 / 更早" by when the item was saved. */
function groupOf(createdAt: number): string {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (createdAt >= start.getTime()) return '今天';
  if (createdAt >= start.getTime() - 6 * DAY) return '最近 7 天';
  return '更早';
}

function nextReview(item: VocabItem): string {
  const ms = item.dueAt - Date.now();
  if (ms <= 0) return '待复习';
  const days = Math.round(ms / DAY);
  if (days < 1) return `${Math.max(1, Math.round(ms / 3_600_000))} 小时后复习`;
  return `${days} 天后复习`;
}

export function VocabPage() {
  const { me } = useAuth();
  const [items, setItems] = useState<VocabItem[] | null>(null);
  const [status, setStatus] = useState<Status>('');
  const [kind, setKind] = useState<'' | 'word' | 'expression'>('');
  const [q, setQ] = useState('');
  const [episode, setEpisode] = useState('');
  const [editing, setEditing] = useState<VocabItem | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load everything once; filtering is instant on the client.
  const load = useCallback(() => api<{ items: VocabItem[] }>('/vocab').then((d) => setItems(d.items), (e) => setError(errorText(e))), []);
  useEffect(() => {
    if (me?.user) void load();
  }, [load, me?.user]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  const episodes = useMemo(() => {
    const map = new Map<string, string>();
    for (const i of items ?? []) for (const c of i.contexts) map.set(c.episodeId, c.episodeTitle);
    return [...map];
  }, [items]);

  const counts = useMemo(() => {
    const all = items ?? [];
    return {
      total: all.length,
      due: all.filter((i) => i.dueAt <= Date.now()).length,
      mastered: all.filter((i) => mastery(i) === 'mastered').length,
    };
  }, [items]);

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (items ?? []).filter((i) => {
      if (kind && i.kind !== kind) return false;
      if (status === 'due' && i.dueAt > Date.now()) return false;
      if (status === 'learning' && mastery(i) === 'mastered') return false;
      if (status === 'mastered' && mastery(i) !== 'mastered') return false;
      if (episode && !i.contexts.some((c) => c.episodeId === episode)) return false;
      if (term && !`${i.surface} ${i.meaning ?? ''} ${i.note ?? ''} ${i.contexts.map((c) => c.en).join(' ')}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [items, kind, status, episode, q]);

  const grouped = useMemo(() => {
    const filtered = q || status || kind || episode;
    if (filtered) return [['', visible]] as [string, VocabItem[]][];
    const map = new Map<string, VocabItem[]>();
    for (const it of visible) map.set(groupOf(it.createdAt), [...(map.get(groupOf(it.createdAt)) ?? []), it]);
    return [...map];
  }, [visible, q, status, kind, episode]);

  if (!me) return <p className="page muted">加载中…</p>;

  return (
    <div className="page vocab">
      <PageHead eyebrow="WORDS IN CONTEXT" title="生词本。" sub="每个词都带着它出现的那句话和原声。" />

      <section className="summary-card">
        <div className="summary-nums">
          <div>
            <b>{counts.total}</b>
            <span>全部</span>
          </div>
          <div className={counts.due ? 'hot' : ''}>
            <b>{counts.due}</b>
            <span>待复习</span>
          </div>
          <div>
            <b>{counts.mastered}</b>
            <span>已掌握</span>
          </div>
        </div>
        <Link className={`button start-review${counts.due ? '' : ' idle'}`} to="/review">
          {counts.due ? `开始复习 · ${counts.due}` : '去复习'}
        </Link>
      </section>

      <div className="vocab-tools">
        <label className="search-box">
          <Icon name="spark" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜索单词、释义、原句" />
        </label>
        <div className="seg-row">
          <div className="pill-seg">
            {STATUS.map(([id, label]) => (
              <button key={id} className={status === id ? 'active' : ''} onClick={() => setStatus(id)}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="chips">
          <button className={`chip${kind === '' ? ' on' : ''}`} onClick={() => setKind('')}>
            单词和表达
          </button>
          <button className={`chip${kind === 'word' ? ' on' : ''}`} onClick={() => setKind('word')}>
            单词
          </button>
          <button className={`chip${kind === 'expression' ? ' on' : ''}`} onClick={() => setKind('expression')}>
            表达
          </button>
          {episodes.length > 1 && (
            <select className="chip-select" value={episode} onChange={(e) => setEpisode(e.target.value)} aria-label="按节目筛选">
              <option value="">所有节目</option>
              {episodes.map(([id, title]) => (
                <option key={id} value={id}>
                  {title}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {error && <p className="status">{error}</p>}
      {!items ? (
        <p className="muted">加载中…</p>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <Icon name="bookmark" />
          </div>
          <h3>还没有生词</h3>
          <p className="muted">精听时点字幕里的单词，或在「核心表达」里收藏，词和原句会一起保存到这里。</p>
          <Link className="button primary" to="/">
            去精听
          </Link>
        </div>
      ) : visible.length === 0 ? (
        <p className="muted center">没有符合条件的内容。</p>
      ) : (
        grouped.map(([group, list]) => (
          <section key={group || 'all'} className="vocab-group">
            {group && (
              <h4>
                {group} <small>{list.length}</small>
              </h4>
            )}
            {list.map((item) => (
              <VocabCard key={item.id} item={item} onEdit={() => setEditing(item)} onChange={load} onToast={setToast} />
            ))}
          </section>
        ))
      )}

      {editing && (
        <EditSheet
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function MasteryDots({ item }: { item: VocabItem }) {
  const m = mastery(item);
  return (
    <span className={`mastery ${m}`} title={`复习 ${item.reviews} 次`}>
      <span className="dots">
        {Array.from({ length: MAX_STAGE + 1 }, (_, i) => (
          <i key={i} className={i < (item.reviews ? item.stage + 1 : 0) ? 'on' : ''} />
        ))}
      </span>
      {MASTERY_LABEL[m]}
    </span>
  );
}

function VocabCard({ item, onEdit, onChange, onToast }: { item: VocabItem; onEdit(): void; onChange(): void; onToast(t: string): void }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? item.contexts : item.contexts.slice(0, 1);
  const removeContext = async (c: VocabContext) => {
    const last = item.contexts.length === 1;
    if (!confirm(last ? '这是最后一个例句，删除后这个词也会从生词本移除。继续？' : '删除这个例句？')) return;
    await api(`/vocab/contexts/${c.id}`, { method: 'DELETE' });
    onChange();
  };
  return (
    <article className="word-card">
      <header>
        <div className="word-title">
          <h3>{item.surface}</h3>
          <span className={`kind ${item.kind}`}>{item.kind === 'expression' ? '表达' : '单词'}</span>
        </div>
        <button className="icon plain" aria-label="编辑" onClick={onEdit}>
          <Icon name="more" />
        </button>
      </header>
      {item.meaning && <p className="word-meaning">{item.meaning}</p>}
      {item.note && <p className="word-note">✎ {item.note}</p>}

      {shown.map((c) => (
        <div key={c.id} className="context">
          <button className="play-dot" aria-label="播放原句" onClick={() => playClip(c).then((how) => how === 'tts' && onToast('原声暂不可用，已用朗读代替'))}>
            <Icon name="play" />
          </button>
          <div className="context-text">
            <p className="en">{highlight(c.en, item.surface)}</p>
            {c.zh && <p className="zh">{c.zh}</p>}
            <div className="context-foot">
              <small>
                {c.programTitle ?? '我的资料'}
                {c.publishedOn ? ` · ${c.publishedOn.slice(5)}` : ''} · {c.episodeTitle}
              </small>
              <span className="context-actions">
                <button
                  className="mini"
                  aria-label="复制"
                  onClick={() =>
                    copyText(formatForCopy({ term: item.surface, meaning: item.meaning ?? undefined, en: c.en, zh: c.zh, program: c.programTitle ?? undefined, episodeTitle: c.episodeTitle, date: c.publishedOn ?? undefined })).then(() =>
                      onToast('已复制，含原句和出处'),
                    )
                  }
                >
                  复制
                </button>
                <Link className="mini" to={`/?ep=${c.episodeId}&t=${c.startMs}`}>
                  原集
                </Link>
                <button className="mini muted-x" aria-label="删除这个例句" onClick={() => removeContext(c)}>
                  ✕
                </button>
              </span>
            </div>
          </div>
        </div>
      ))}
      {item.contexts.length > 1 && (
        <button className="plain more-contexts" onClick={() => setExpanded(!expanded)}>
          {expanded ? '收起' : `还有 ${item.contexts.length - 1} 个例句`}
        </button>
      )}

      <footer>
        <MasteryDots item={item} />
        <small className="muted">{nextReview(item)}</small>
      </footer>
    </article>
  );
}

function EditSheet({ item, onClose, onSaved }: { item: VocabItem; onClose(): void; onSaved(): void }) {
  const [meaning, setMeaning] = useState(item.meaning ?? '');
  const [note, setNote] = useState(item.note ?? '');
  const [msg, setMsg] = useState<string | null>(null);
  const save = async () => {
    try {
      await api(`/vocab/${item.id}`, { method: 'PATCH', json: { meaning, note } });
      onSaved();
    } catch (e) {
      setMsg(errorText(e));
    }
  };
  const remove = async () => {
    if (!confirm(`从生词本删除「${item.surface}」和它的全部例句？`)) return;
    await api(`/vocab/${item.id}`, { method: 'DELETE' });
    onSaved();
  };
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <section className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="编辑生词">
        <header className="row spread">
          <h3 className="term">{item.surface}</h3>
          <button className="plain" onClick={onClose} aria-label="关闭">
            ✕
          </button>
        </header>
        <label className="field">
          <span>释义</span>
          <input value={meaning} onChange={(e) => setMeaning(e.target.value)} />
        </label>
        <label className="field">
          <span>备注</span>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="自己的理解、联想、造句" />
        </label>
        <p className="muted small-note">
          已复习 {item.reviews} 次 · 下次复习间隔 {INTERVAL_DAYS[Math.min(item.stage, INTERVAL_DAYS.length - 1)]} 天
        </p>
        <div className="row spread">
          <button className="plain danger-text" onClick={remove}>
            <Icon name="trash" /> 删除
          </button>
          <button className="primary" onClick={save}>
            保存
          </button>
        </div>
        {msg && <p className="status">{msg}</p>}
      </section>
    </div>
  );
}

function highlight(sentence: string, term: string) {
  const at = findTerm(sentence, term);
  if (!at) return sentence;
  return (
    <>
      {sentence.slice(0, at.start)}
      <mark>{sentence.slice(at.start, at.end)}</mark>
      {sentence.slice(at.end)}
    </>
  );
}
